/**
 * The state timeline and timeline panel types. Both draw spans of time on a shared axis from the
 * same settings, so they share a file and one settings block.
 */

const statetimeline = {
  names: [
    {
      id: 'statetimeline',
      label: 'State timeline',
      phrase: 'a state timeline',
      guide: 'statetimeline.md',
      advanced: true,
      toolSummary: 'how long each record spent in each state, one row per span, up to 20 records',
      promptSummary: 'how long each record spent in each state and where it is stuck now: every record is one line on a shared time axis, split into coloured spans, one row per span and up to 20 records'
    },
    /* The same drawing answering a different question, which is what earns a name rather than a
       field: this one is about which records run at the same time, so it carries no state at all. */
    {
      id: 'timeline',
      label: 'Timeline',
      phrase: 'a timeline',
      guide: 'timeline.md',
      advanced: true,
      toolSummary: 'which records overlap in time, one bar per record from its start to its end, up to 20 records',
      promptSummary: 'which records overlap in time and which follow one another: every record is one bar on a shared time axis, running from its start to its end, one row per record and up to 20 records'
    }
  ],
  block: 'statetimeline',
  /* State timeline and timeline. The query already returns one row per span; this block identifies
     the record the span belongs to, the state it was in, and both ends of the time it lasted. There
     is no field for the axis or for the colours: the axis spans the earliest start to the latest
     end, and the colour comes from the state's position in the shared series palette, or is one
     theme colour for every bar when there is no state column. */
  fields: {
    labelKey: { kind: 'string', required: true, hint: 'a span needs the record it belongs to, the state it was in, and both ends of the time it lasted', label: 'Row label' },
    /* Only the stateful name has states to colour; the overlap name is one plain bar per record,
       so the column is cleared rather than required. Which name owes it is in `validate`. */
    stateKey: { kind: 'string', clearable: true, label: 'State' },
    startKey: { kind: 'string', required: true, hint: 'a span needs the record it belongs to, the state it was in, and both ends of the time it lasted', label: 'Started' },
    endKey: { kind: 'string', required: true, hint: 'a span needs the record it belongs to, the state it was in, and both ends of the time it lasted', label: 'Ended' }
  },
  validate: ({ block, type }) => {
    /* A state timeline with no state column is a row of unexplained bars, and the same column is
       meaningless to the name that only asks which records overlap. Both names share one block, so
       the spec itself cannot say this. */
    if (type === 'statetimeline' && (typeof block.stateKey !== 'string' || !block.stateKey)) {
      return 'statetimeline.stateKey must be a non-empty string for a state timeline: every span needs the state it was in.';
    }

    return '';
  },
  editorHint: "One row is one span of time in one state, and every row sharing a label stacks onto that record's line. The panel only draws a span that has both ends, so close the state a record is still in with the current time in SQL.",
  /* Which columns carry the two ends is named because a timeline with no end column is the way this
     type fails: the assistant needs to see that the query owes the panel a closed span before it
     changes anything else. The state is named only when the block carries one, so a stateless
     timeline reads as what it is rather than as a half-configured state timeline. */
  stateSummary: ({ block }) => (block.labelKey && block.startKey && block.endKey ? `statetimeline: ${block.labelKey}${block.stateKey ? ` × ${block.stateKey}` : ''}, ${block.startKey} to ${block.endKey}` : ''),
  toolSchema: {
    type: ['object', 'null'],
    additionalProperties: false,
    required: ['labelKey', 'startKey', 'endKey'],
    description: 'Timeline mapping (displayType "statetimeline" or "timeline"): ONE ROW IS ONE SPAN of time on a record\'s line, and every row sharing a label stacks onto that line. "statetimeline" splits each record into its states and REQUIRES a state column; "timeline" asks which records overlap, so it is one row per record and takes no state. The panel draws only spans that carry BOTH ends, so the query owes it a start and an end per span: derive the end with LEAD(started_at) OVER (PARTITION BY record ORDER BY started_at) and COALESCE the open one to now(). Times must be timestamps or ISO strings; a bare epoch number is ambiguous between seconds and milliseconds and is counted as undrawable rather than guessed. Capped at 20 records, with anything not drawn counted below the rows. Read immutable/analytics/panel-types/<statetimeline|timeline>.md before configuring one.',
    properties: {
      labelKey: { type: 'string', description: 'Column naming the record whose line this span sits on. Rows sharing a value share a line. Dot paths work.' },
      stateKey: {
        type: ['string', 'null'],
        description: 'Column naming the state the record was in for this span. REQUIRED for "statetimeline"; leave it out for "timeline", whose bars are one colour because the question is when records overlap rather than what they were doing. Its distinct values become the colours and the legend, so keep the set small: past eight states the palette repeats a colour. Dot paths work.'
      },
      startKey: { type: 'string', description: 'Column holding the timestamp the span began. Dot paths work.' },
      endKey: { type: 'string', description: 'Column holding the timestamp the span ended. Dot paths work. A span whose end is missing, unparseable, or not after its start is counted below the rows rather than drawn, so close an ongoing state with now() in the query.' }
    }
  }
};

export default statetimeline;
